/**
 * Pase de fotos del hero.
 *
 * Todas las fotos principales de los productos disponibles pasan en orden
 * aleatorio con un fundido cruzado largo. El orden se baraja en el navegador en
 * cada visita (Liquid no tiene aleatorio y la página se cachea), y la primera
 * foto ya llega elegida desde el servidor para que se pinte sin esperar a JS.
 *
 * Fundido cruzado de verdad: la foto nueva entra por encima de la anterior, que
 * sigue opaca debajo hasta que la nueva termina. Si las dos se fundieran a la
 * vez, a mitad de camino ambas estarían semitransparentes y se vería el fondo:
 * un parpadeo gris entre fotos.
 *
 * Carga bajo demanda: sólo la primera foto viene con srcset; las demás lo traen
 * en data-* y se completan justo antes de mostrarse, esperando a que estén
 * decodificadas. Nadie se descarga 38 fotos a pantalla completa para ver dos.
 *
 * Se detiene cuando no hay nadie mirando (pestaña oculta o hero fuera de
 * pantalla) y respeta a quien pide reducir movimiento: arranca en pausa. El
 * botón de pausa no es decoración: un contenido que se mueve solo más de cinco
 * segundos tiene que poder detenerse (WCAG 2.2.2).
 */

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function shuffle(list) {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

class ScHeroSlides extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.sc-hero__slide')];
    if (this.slides.length < 2) return;

    this.hero = this.closest('.sc-hero');
    this.interval = Number(this.dataset.interval) || 6000;
    this.fade = Number(this.dataset.fade) || 2000;
    this.style.setProperty('--sc-hero-fade', `${this.fade}ms`);

    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.queue = [];

    /* El nombre que le toca, el de dentro. En escritorio hay tres pases en el
       mismo hero y `hero.querySelector` les devolvía a los tres el primero: las
       tres columnas nombraban la pieza de la primera. El hero sigue como
       respaldo para la versión de una sola foto. */
    this.piece = this.querySelector('[data-hero-piece]') || this.hero?.querySelector('[data-hero-piece]');
    this.pieceTitle = this.piece?.querySelector('[data-hero-piece-title]');

    /* Y el turno. Si las tres columnas cambiaran en el mismo instante se leería
       como un salto de página en lugar de un escaparate: cada una lleva su parte
       del intervalo de retraso. */
    const hermanas = [...(this.parentElement?.querySelectorAll('sc-hero-slides') || [])];
    this.turno = Math.max(0, hermanas.indexOf(this));
    this.cuantasColumnas = hermanas.length || 1;
    this.origen = Date.now();
    this.pauseButton = this.hero?.querySelector('[data-hero-pause]');

    this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.onScreen = true;

    if (this.pauseButton) {
      this.pauseButton.hidden = false;
      this.onPauseClick = () => {
        this.userPaused = !this.userPaused;
        this.syncPauseButton();
        this.schedule();
      };
      this.pauseButton.addEventListener('click', this.onPauseClick);
      this.syncPauseButton();
    }

    this.onVisibility = () => this.schedule();
    document.addEventListener('visibilitychange', this.onVisibility);

    if ('IntersectionObserver' in window && this.hero) {
      this.observer = new IntersectionObserver(([entry]) => {
        this.onScreen = entry.isIntersecting;
        this.schedule();
      });
      this.observer.observe(this.hero);
    }

    /* Y si la pantalla crece y aparecen las columnas que estaban ocultas, hay que
       completarles la foto: sin esto se quedarían en blanco hasta su primer
       cambio, hasta seis segundos después. */
    this.ancha = window.matchMedia('(min-width: 750px)');
    this.onAncho = () => {
      this.mostrarActiva();
      this.schedule();
    };
    this.ancha.addEventListener('change', this.onAncho);

    this.mostrarActiva();
    this.preloadUpcoming();
    this.schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.pauseButton?.removeEventListener('click', this.onPauseClick);
    this.ancha?.removeEventListener('change', this.onAncho);
  }

  /* `offsetParent === null` es «esta columna no ocupa sitio»: en el teléfono la
     segunda y la tercera están en `display: none`. Una columna oculta no corre
     —ni reloj, ni precarga, ni fotos bajadas— porque todo eso sería trabajo y
     megas para algo que nadie va a ver. */
  get visible() {
    return this.offsetParent !== null;
  }

  get running() {
    return !this.userPaused && this.onScreen && !document.hidden && this.visible;
  }

  syncPauseButton() {
    this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
    this.pauseButton.setAttribute(
      'aria-label',
      this.userPaused ? 'Reanudar el pase de fotos' : 'Pausar el pase de fotos'
    );
  }

  /* El reloj va por fase, no por «dentro de N milisegundos».
   *
   * Los momentos de cambio son origen + desfase + k·intervalo, y aquí sólo se
   * busca el siguiente. Eso es lo que mantiene el desfase: `schedule` se llama
   * muchas veces —al entrar el hero en pantalla, al volver a la pestaña, al
   * terminar cada fundido— y con un retraso relativo cada llamada lo borraba.
   * Las tres columnas se reprogramaban en el mismo instante y acababan cambiando
   * a la vez, que es exactamente lo que había que evitar.
   */
  schedule() {
    clearTimeout(this.timer);
    if (!this.running || this.busy) return;
    const desfase = Math.round((this.interval * this.turno) / this.cuantasColumnas);
    let espera = desfase - (Date.now() - this.origen);
    // Nunca dos cambios casi seguidos: si el momento ya pasó —el fundido anterior
    // se comió su turno— se espera al siguiente.
    while (espera < 400) espera += this.interval;
    this.timer = setTimeout(() => this.advance(), espera);
  }

  /* Baraja todas menos la que está en pantalla, para no repetir foto seguida ni
     al empezar una vuelta nueva. Cada foto sale una vez por vuelta. */
  refillQueue() {
    this.queue = shuffle(this.slides.map((_, i) => i).filter((i) => i !== this.index));
  }

  upcoming() {
    if (this.queue.length === 0) this.refillQueue();
    return this.queue[0];
  }

  hydrate(slide) {
    const images = [...slide.querySelectorAll('img[data-srcset]')];
    for (const img of images) {
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      delete img.dataset.srcset;
      delete img.dataset.src;
      delete img.dataset.sizes;
    }
    const all = [...slide.querySelectorAll('img')];
    const decoded = Promise.all(all.map((img) => (img.decode ? img.decode().catch(() => {}) : null)));
    // Una foto que no llega en cinco segundos no puede congelar el pase.
    return Promise.race([decoded, wait(5000)]);
  }

  preloadUpcoming() {
    if (!this.visible) return;
    const next = this.slides[this.upcoming()];
    if (next) this.hydrate(next);
  }

  /* La foto que ya está puesta. En la primera columna viene escrita en el HTML y
     aquí no hay nada que hacer; en las otras dos llega en diferido y se completa
     en cuanto se ven, que en escritorio es de entrada. */
  mostrarActiva() {
    if (!this.visible) return;
    const activa = this.slides[this.index];
    if (activa?.querySelector('img[data-srcset]')) this.hydrate(activa);
  }

  async advance() {
    if (this.busy || !this.running) return;
    this.busy = true;

    const nextIndex = this.upcoming();
    const next = this.slides[nextIndex];
    const current = this.slides[this.index];

    await this.hydrate(next);

    if (!this.running) {
      this.busy = false;
      return;
    }

    this.queue.shift();
    next.removeAttribute('aria-hidden');
    next.classList.add('is-entering');
    this.updatePiece(next);

    await wait(this.fade);

    current.classList.remove('is-active');
    current.setAttribute('aria-hidden', 'true');
    next.classList.add('is-active');
    next.classList.remove('is-entering');
    this.index = nextIndex;

    this.busy = false;
    this.preloadUpcoming();
    this.schedule();
  }

  /* El nombre de la pieza cambia con la foto: se apaga, cambia y vuelve, en la
     primera mitad del fundido para que nunca nombre una foto que ya se fue. */
  async updatePiece(slide) {
    if (!this.piece || !this.pieceTitle) return;
    this.piece.classList.add('is-changing');
    await wait(Math.min(500, this.fade / 2));
    this.pieceTitle.textContent = slide.dataset.title || '';
    if (slide.dataset.url) this.piece.href = slide.dataset.url;
    this.piece.classList.remove('is-changing');
  }
}

if (!customElements.get('sc-hero-slides')) {
  customElements.define('sc-hero-slides', ScHeroSlides);
}
